import React, { useState } from 'react';
import { Heart, Star, Sparkles, Percent } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useWishlist } from '../context/WishlistContext';
import { useCurrency } from '../context/CurrencyContext';

const BADGE_META = {
  bestseller: { label: 'Bestseller', bg: 'bg-amber-500',  text: 'text-white', Icon: Star },
  new:        { label: 'New',        bg: 'bg-emerald-600', text: 'text-white', Icon: Sparkles },
  sale:       { label: 'Sale',       bg: 'bg-rose-500',   text: 'text-white', Icon: Percent },
};

const ProductCard = ({ product, onView }) => {
  const { fmt } = useCurrency();
  const { addToCart } = useApp();
  const wishlist = useWishlist();
  const [src, setSrc] = useState(product.image || product.fallbackImage || '');
  const [failed, setFailed] = useState(!src);
  const handleErr = () => {
    if (product.fallbackImage && src !== product.fallbackImage) {
      setSrc(product.fallbackImage);
    } else {
      setFailed(true);
    }
  };
  const saved = wishlist?.has(product.id) || false;
  const badge = BADGE_META[product.badge];

  return (
    <div data-testid={`product-card-${product.id}`} className="group relative rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] overflow-hidden card-lift hover:border-brand flex flex-col">
      {product.isRx && (
        <span className="absolute top-2 left-2 bg-white/90 dark:bg-neutral-800/90 text-neutral-700 dark:text-neutral-200 text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded z-10 border border-neutral-200 dark:border-neutral-700">Rx</span>
      )}
      {badge && (
        <span
          data-testid={`product-badge-${product.id}`}
          className={`absolute top-2 ${product.isRx ? 'left-10' : 'left-2'} inline-flex items-center gap-1 ${badge.bg} ${badge.text} text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded z-10 shadow-sm uppercase`}
        >
          <badge.Icon size={9} strokeWidth={2.5} /> {badge.label}
        </span>
      )}
      <button
        data-testid={`wishlist-toggle-${product.id}`}
        onClick={(e) => { e.stopPropagation(); wishlist?.toggle(product); }}
        aria-label={saved ? 'Remove from wishlist' : 'Save to wishlist'}
        title={saved ? 'Remove from wishlist' : 'Save to wishlist'}
        className={`absolute top-2 right-2 z-10 w-8 h-8 rounded-full border transition-all flex items-center justify-center ${saved ? 'bg-rose-500 border-rose-500 text-white scale-105' : 'bg-white/90 dark:bg-neutral-800/90 border-neutral-200 dark:border-neutral-700 text-neutral-500 hover:text-rose-500 hover:border-rose-300'}`}
      >
        <Heart size={14} fill={saved ? 'currentColor' : 'none'} strokeWidth={2} />
      </button>
      <div className="aspect-square bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center overflow-hidden">
        {!failed && src ? (
          <img src={src} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" onError={handleErr} />
        ) : (
          <div className="w-full text-center text-neutral-400 dark:text-neutral-500 p-4">
            <div className="text-[11px] tracking-[0.15em] uppercase mb-1 opacity-60">{product.catLabel}</div>
            <div className="font-serif text-[20px] leading-tight text-neutral-500 dark:text-neutral-400">{product.name}</div>
            <div className="text-[12.5px] mt-1 opacity-70">{product.strength}</div>
          </div>
        )}
      </div>
      <div className="p-3.5 flex flex-col flex-1">
        <div className="text-[10px] tracking-[0.1em] text-neutral-500 dark:text-neutral-400 mb-1 uppercase truncate">{product.catLabel}</div>
        <h3 className="text-[15px] font-medium text-neutral-900 dark:text-neutral-100 mb-1 line-clamp-2 min-h-[38px]">{product.name}</h3>
        <div className="text-[12.5px] text-neutral-500 dark:text-neutral-400 mb-2">{product.strength}{product.packaging ? ' - ' + product.packaging : ''}</div>
        <div className="mb-3">
          <span className="text-neutral-900 dark:text-neutral-100 text-[17px] font-semibold tabular-nums">{fmt(product.price)}</span>
        </div>
        <div className="mt-auto flex gap-2">
          <button onClick={() => onView(product)} className="flex-1 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-[13px] rounded-md py-1.5 hover:border-brand hover:text-brand transition-colors">View</button>
          <button data-testid={`add-to-cart-${product.id}`} onClick={() => addToCart(product)} className="btn-brand flex-1 text-[13px] font-semibold rounded-md py-1.5">Add to cart</button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
